"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, X } from "lucide-react";
import BottomSheetModal from "@/src/shared/components/BottomSheetModal";
import { useWindowSize } from "@/src/shared/hooks/useWindowSize";

interface IResponsiveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Shows a back arrow in the header (a pushed screen, like the create sheet). */
  onBack?: () => void;
  /**
   * Keep one height whatever the content (90% of the screen on mobile, 80% on desktop),
   * for lists that change under the user, like reviews filtered by a topic.
   */
  fixedHeight?: boolean;
  /** Desktop width: 480px, or 680px for reading-heavy content like reviews. */
  wide?: boolean;
  children: React.ReactNode;
}

/**
 * A dialog that is the app's bottom sheet on mobile and a centred card on desktop
 * (wishlist sheets, reviews). One variant is mounted at a time, as the login modal does.
 */
const ResponsiveDialog: React.FC<IResponsiveDialogProps> = ({
  isOpen,
  onClose,
  title,
  onBack,
  fixedHeight = false,
  wide = false,
  children,
}) => {
  const isDesktop = useWindowSize().width >= 768;
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!isOpen || !isDesktop) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, isDesktop, onClose]);

  if (!isDesktop) {
    return (
      <BottomSheetModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        showBackButton={!!onBack}
        onBack={onBack}
        height={fixedHeight ? "tall" : "auto"}
      >
        {children}
      </BottomSheetModal>
    );
  }
  if (!mounted) return null;

  // Portalled above page chrome, like BottomSheetModal.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[1000000] bg-black/50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="pointer-events-none fixed inset-0 z-[1000000] flex items-center justify-center p-4"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label={title}
              className={`pointer-events-auto flex w-full ${wide ? "max-w-[680px]" : "max-w-[480px]"} flex-col overflow-hidden rounded-3xl bg-white shadow-2xl ${fixedHeight ? "h-[80vh]" : "max-h-[80vh]"}`}
            >
              <div className="flex items-center justify-between gap-2 px-6 pb-2 pt-5">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    aria-label="Back"
                    className="-ml-2 flex h-9 w-9 items-center justify-center rounded-full text-ink-black hover:bg-gray-100"
                  >
                    <ChevronLeft size={22} />
                  </button>
                )}
                <h2 className="flex-1 text-xl font-semibold text-ink-black">
                  {title}
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-ink-black hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ResponsiveDialog;
